/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */
import {
	CheckIcon,
	Combobox,
	Group,
	Pill,
	PillsInput,
	PillsInputProps,
	useCombobox,
} from '@mantine/core';
import { useState } from 'react';

// Style
import { capitalizeString } from '@utils/functions';
import classes from './TagsInput.module.css';

import { NOT_APPLICABLE } from '@config/constants/common';
import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/* Type                                    */
/* -------------------------------------------------------------------------- */

type TItem = {
	label: string;
	color: string;
	value: string;
};

type TTagsInputProps = PillsInputProps & {
	data: TItem[];
	placeholder?: string;
	onChange?: (val: string[]) => void;
	value: any;
};

/* -------------------------------------------------------------------------- */
/* Component                                 */
/* -------------------------------------------------------------------------- */
export const TagsInput = (props: TTagsInputProps) => {
	const { t } = useTranslation();

	const combobox = useCombobox({
		onDropdownClose: () => {
			combobox.resetSelectedOption();
		},
		onDropdownOpen: () =>
			combobox.updateSelectedOptionIndex('active'),
	});

	const [search, setSearch] = useState('');

	const handleValueSelect = (_tagLabel: string) => {
		if (props && props.onChange) {
			const foundTag =
				props?.data?.find(
					(item) =>
						item.label.toLowerCase() ==
						_tagLabel.toLowerCase()
				)?.value || NOT_APPLICABLE;

			const currentValue = props.value || [];

			if (
				currentValue &&
				Array.isArray(currentValue) &&
				!currentValue.find(
					(_tagValue: string) => _tagValue == foundTag
				) &&
				currentValue.length == 2
			) {
				return;
			}
			const body = currentValue.includes(foundTag)
				? currentValue.filter((v: any) => v !== foundTag)
				: [...currentValue, foundTag];
			props.onChange(body);
		}
	};

	const handleValueRemove = (_tagValue: string) => {
		if (props && props.onChange) {
			const currentValue = props.value;
			const body = currentValue.filter(
				(v: string) => v !== _tagValue
			);
			props.onChange(body);
		}
	};

	const values =
		props?.value &&
		Array.isArray(props.value) &&
		props.value.map((item: string) => {
			const tagData = props.data?.find(
				(t) => t.value === item
			);
			return (
				<Pill
					key={item}
					data-testid={'pill-' + item}
					classNames={{ root: classes.pill }}
					styles={{
						root: {
							backgroundColor:
								'var(--mantine-color-gray-1)',
						},
					}}
					withRemoveButton
					onRemove={() => handleValueRemove(item)}
				>
					<Group gap={4} wrap="nowrap">
						<span
							style={{ color: tagData?.color || 'gray' }}
						>
							●
						</span>
						<span className={classes.labelText}>
							{capitalizeString(
								tagData?.label || NOT_APPLICABLE
							)}
						</span>
					</Group>
				</Pill>
			);
		});

	const options =
		props?.data &&
		Array.isArray(props.data) &&
		props.data
			.filter((item: TItem) =>
				item.label
					.toLowerCase()
					.includes(search.trim().toLowerCase())
			)
			.map((item: TItem) => (
				<Combobox.Option
					data-testid={item.label}
					value={item.label}
					key={item.value}
					active={props.value.includes(item.value)}
				>
					<Group gap="sm" wrap="nowrap">
						{props.value.includes(item.value) ? (
							<CheckIcon size={12} />
						) : null}
						<Group gap={7} wrap="nowrap">
							<span style={{ color: item.color }}>●</span>
							<span>{capitalizeString(item.label)}</span>
						</Group>
					</Group>
				</Combobox.Option>
			));

	return (
		<Combobox
			store={combobox}
			onOptionSubmit={handleValueSelect}
		>
			<Combobox.DropdownTarget>
				<PillsInput
					classNames={{
						label: classes.label,
						input: classes.inputPillInput,
						error: classes.error,
					}}
					{...props}
					onClick={() => combobox.openDropdown()}
					rightSection={
						<IconChevronDown
							color="var(--mantine-color-igluPrimary-9)"
							stroke={1.5}
							style={{ marginRight: '5px' }}
							size="18px"
						/>
					}
				>
					<Pill.Group>
						{values}
						{(props.value?.length || 0) < 2 && (
							<Combobox.EventsTarget>
								<PillsInput.Field
									className={classes.input}
									onFocus={() => combobox.openDropdown()}
									onBlur={() => combobox.closeDropdown()}
									value={search}
									placeholder={props.placeholder}
									onChange={(event) => {
										combobox.updateSelectedOptionIndex();
										setSearch(event.currentTarget.value);
									}}
									onKeyDown={(event) => {
										if (
											event.key === 'Backspace' &&
											search.length === 0
										) {
											event.preventDefault();
											handleValueRemove(
												props.value[
													props.value.length - 1
												]
											);
										}
									}}
								/>
							</Combobox.EventsTarget>
						)}
					</Pill.Group>
				</PillsInput>
			</Combobox.DropdownTarget>

			<Combobox.Dropdown>
				<Combobox.Options
					mah={200}
					style={{ overflowY: 'auto' }}
				>
					{options && options.length > 0 ? (
						options
					) : (
						<Combobox.Empty>
							{t('global.noData')}
						</Combobox.Empty>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
